import { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  Menu, X, ChevronDown, ArrowRight, ArrowUpRight, Languages,
  LayoutGrid, HeartPulse, Home as HomeIcon, Building2, PiggyBank,
  Plane, KeyRound, Briefcase, Car, RefreshCw, Baby, Rocket, Scale, Sparkles,
} from 'lucide-react';
import { BRAND, NAV, GUIDE_HIGHLIGHTS } from '@/data/site';
import { NAV_EN } from '@/data/en/site';
import { useLang, LangLink } from '@/i18n/LangContext';
import { SALUTE_PAGES } from '@/data/salute';
import { PRIVATI_PAGES } from '@/data/privati';
import { AZIENDE_PAGES, PREVIDENZA_PAGE } from '@/data/aziende';
import { SALUTE_PAGES_EN } from '@/data/en/salute';
import { PRIVATI_PAGES_EN } from '@/data/en/privati';
import { AZIENDE_PAGES_EN, PREVIDENZA_PAGE_EN } from '@/data/en/aziende';
import { GUIDE_IMAGES } from '@/data/site';
import { SOLUTION_MENU, SOLUTION_MENU_EN } from '@/data/solutionMenu';

const ICON_MAP = {
  '/soluzioni': LayoutGrid,
  '/cassa-malati': HeartPulse,
  '/soluzioni/privati': HomeIcon,
  '/soluzioni/aziende': Building2,
  '/soluzioni/previdenza': PiggyBank,
  '/guide': LayoutGrid,
  '/guide/privati/trasferirsi-in-svizzera': Plane,
  '/guide/privati/andare-a-vivere-da-soli': KeyRound,
  '/guide/privati/comprare-casa-a-30-anni': HomeIcon,
  '/guide/privati/mettersi-in-proprio': Briefcase,
  '/guide/privati/importare-auto-in-svizzera': Car,
  '/guide/salute/cambio-cassa-malati-premi-disdetta': RefreshCw,
  '/guide/salute/assicurazione-prenatale': Baby,
  '/guide/aziende/aprire-un-azienda': Rocket,
  '/guide/aziende/forme-giuridiche-svizzera': Scale,
};

const IT_REGISTRY = [...SALUTE_PAGES, ...PRIVATI_PAGES, ...AZIENDE_PAGES, PREVIDENZA_PAGE];
const EN_REGISTRY = [...SALUTE_PAGES_EN, ...PRIVATI_PAGES_EN, ...AZIENDE_PAGES_EN, PREVIDENZA_PAGE_EN];

const SolutionsMegaPanel = () => {
  const { lang, t } = useLang();
  const menu = lang === 'en' ? SOLUTION_MENU_EN : SOLUTION_MENU;
  const [activeKey, setActiveKey] = useState(menu.areas[0].key);
  const activeArea = menu.areas.find((area) => area.key === activeKey) || menu.areas[0];
  const ActiveIcon = ICON_MAP[activeArea.href] || Sparkles;

  return (
    <div className="absolute left-0 right-0 top-full invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-[opacity,visibility] duration-200 bg-white border-b border-border shadow-[0_40px_60px_-30px_rgba(10,61,32,0.25)] max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain">
      <div className="mx-auto max-w-site px-8 py-8 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-3 lg:border-r border-border lg:pr-8">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-5">{menu.overview.label}</p>
          <LangLink
            to={menu.overview.href}
            data-testid="mega-solutions-overview"
            className="group/overview block rounded-xl bg-brand-deep text-white p-5 hover:bg-primary transition-colors duration-200"
          >
            <span className="inline-flex bg-white/10 p-2.5 rounded-lg mb-5">
              <LayoutGrid className="h-5 w-5 text-brand-light" />
            </span>
            <span className="block font-heading text-xl font-bold tracking-tight mb-2">{menu.overview.label}</span>
            <span className="block text-sm leading-relaxed text-white/65">{menu.overview.description}</span>
            <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-brand-light">
              {t('explore')} <ArrowRight className="h-4 w-4 transition-transform group-hover/overview:translate-x-1" />
            </span>
          </LangLink>
        </div>

        <div className="lg:col-span-4 lg:border-r border-border lg:pr-8">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-5">{menu.areasLabel}</p>
          <div className="space-y-2">
            {menu.areas.map((area) => {
              const Icon = ICON_MAP[area.href] || Sparkles;
              const isActive = area.key === activeArea.key;
              return (
                <LangLink
                  key={area.key}
                  to={area.href}
                  data-testid={`mega-solutions-area-${area.key}`}
                  onMouseEnter={() => setActiveKey(area.key)}
                  onFocus={() => setActiveKey(area.key)}
                  className={`flex items-start gap-3 rounded-lg p-3 transition-colors duration-150 ${isActive ? 'bg-brand-pale text-primary' : 'hover:bg-brand-pale/60'}`}
                >
                  <span className={`p-2 rounded-lg shrink-0 ${isActive ? 'bg-white' : 'bg-brand-pale'}`}>
                    <Icon className="h-4 w-4 text-primary" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-foreground">{area.label}</span>
                    <span className="block mt-0.5 text-xs leading-relaxed text-foreground/55">{area.description}</span>
                  </span>
                  <ArrowRight className={`h-4 w-4 ml-auto mt-2 shrink-0 transition-transform ${isActive ? 'translate-x-1 text-primary' : 'text-foreground/30'}`} />
                </LangLink>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-5 min-w-0" aria-live="polite">
          <div className="flex items-start justify-between gap-5 mb-5">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-2">{menu.productsLabel}</p>
              <h3 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
                <ActiveIcon className="h-5 w-5 text-brand-light" />
                {activeArea.label}
              </h3>
            </div>
            <span className="hidden xl:block max-w-[170px] text-right text-[11px] leading-relaxed text-foreground/45">{menu.productsHint}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-5 gap-y-1">
            {activeArea.products.map((product) => (
              product.href ? (
                <LangLink
                  key={product.label}
                  to={product.href}
                  className="group/product flex items-start gap-2 py-2 text-xs leading-snug font-medium text-foreground/70 hover:text-primary transition-colors"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brand-light shrink-0" />
                  {product.label}
                  <ArrowUpRight className="h-3.5 w-3.5 ml-auto shrink-0 opacity-0 group-hover/product:opacity-100 transition-opacity" />
                </LangLink>
              ) : (
                <span key={product.label} className="flex items-start gap-2 py-2 text-xs leading-snug text-foreground/65">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brand-light shrink-0" />
                  {product.label}
                </span>
              )
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MegaPanel = ({ item }) => {
  const { lang, t } = useLang();
  const isGuides = item.href === '/guide';
  if (item.href === '/soluzioni') return <SolutionsMegaPanel />;
  const registry = lang === 'en' ? EN_REGISTRY : IT_REGISTRY;
  const highlights = isGuides
    ? GUIDE_HIGHLIGHTS.map((p) => registry.find((page) => page.path === p)).filter(Boolean)
    : [];

  return (
    <div className="absolute left-0 right-0 top-full invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-[opacity,visibility] duration-200 bg-white border-b border-border shadow-[0_40px_60px_-30px_rgba(10,61,32,0.25)]">
      <div className={`mx-auto max-w-site px-8 py-9 grid gap-10 ${isGuides ? 'lg:grid-cols-12' : ''}`}>
        <div className={isGuides ? 'lg:col-span-7' : ''}>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-5">{item.label}</p>
          <div className={`grid ${isGuides ? 'gap-1.5 sm:grid-cols-2' : 'gap-3 sm:grid-cols-2 lg:grid-cols-5'}`}>
            {item.children.map((c) => {
              const Icon = ICON_MAP[c.href] || Sparkles;
              return (
                <LangLink
                  key={c.href}
                  to={c.href}
                  data-testid={`mega-${c.href.replace(/\//g, '-')}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-brand-pale/70 transition-colors duration-150 group/link"
                >
                  <span className="bg-brand-pale p-2.5 rounded-lg shrink-0 group-hover/link:bg-white transition-colors duration-150">
                    <Icon className="h-4.5 w-4.5 h-[18px] w-[18px] text-primary" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-foreground/85 group-hover/link:text-primary transition-colors duration-150">{c.label}</span>
                    {c.description && <span className="block mt-1 text-xs leading-relaxed text-foreground/55">{c.description}</span>}
                  </span>
                </LangLink>
              );
            })}
          </div>
        </div>

        {isGuides && (
          <div className="lg:col-span-5 lg:border-l border-border lg:pl-10">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-5">{t('highlights')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
              {highlights.map((g) => (
                <LangLink
                  key={g.path}
                  to={g.path}
                  data-testid={`mega-highlight-${g.path.replace(/\//g, '-')}`}
                  className="group/hl block"
                >
                  <div className="overflow-hidden rounded-lg mb-3 aspect-[16/9]">
                    <img
                      src={GUIDE_IMAGES[g.path]}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover/hl:scale-105"
                    />
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-light mb-1">{g.area}</p>
                  <p className="font-heading text-sm font-bold tracking-tight leading-snug group-hover/hl:text-primary transition-colors duration-150 text-balance">{g.title}</p>
                </LangLink>
              ))}
            </div>
            <LangLink
              to="/guide"
              data-testid="mega-all-guides"
              className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-primary group/all"
            >
              {t('allGuides')}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/all:translate-x-1" />
            </LangLink>
          </div>
        )}
      </div>
    </div>
  );
};

const DesktopDropdown = ({ item }) => (
  <div className="group" key={item.label}>
    <button
      data-testid={`nav-${item.label.toLowerCase()}-trigger`}
      className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors duration-200"
    >
      {item.label}
      <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
    </button>
    <MegaPanel item={item} />
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const location = useLocation();
  const { lang, lp, t, switchPath } = useLang();
  const navItems = lang === 'en' ? NAV_EN : NAV;

  const langSwitch = (
    <Link
      to={switchPath(lang === 'en' ? 'it' : 'en')}
      data-testid="lang-switch"
      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-foreground/70 hover:text-primary transition-colors duration-200"
      aria-label={lang === 'en' ? 'Passa all\u2019italiano' : 'Switch to English'}
    >
      <Languages className="h-4 w-4" />
      {lang === 'en' ? 'IT' : 'EN'}
    </Link>
  );

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 relative bg-white border-b border-border">
      <div className="mx-auto max-w-site px-5 md:px-8 flex items-center justify-between h-16 md:h-20">
        <LangLink to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0" onClick={() => setOpen(false)}>
          <img src={BRAND.logoExtended} alt="Unity Consulting" className="h-8 md:h-10 w-auto" />
        </LangLink>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Navigazione principale">
          {navItems.map((item) =>
            item.children ? (
              <DesktopDropdown key={item.label} item={item} />
            ) : (
              <NavLink
                key={item.label}
                to={lp(item.href)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}-link`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive ? 'text-primary' : 'text-foreground/80 hover:text-primary'}`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          {langSwitch}
          <LangLink
            to="/prenota"
            data-testid="header-cta-button"
            className="hidden sm:inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold rounded-lg hover:bg-brand-dark transition-colors duration-200 group"
          >
            {t('ctaHeader')}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </LangLink>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-foreground"
            onClick={() => setOpen(!open)}
            aria-label={open ? t('menuClose') : t('menuOpen')}
            aria-expanded={open}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-border bg-white max-h-[calc(100vh-4rem)] overflow-y-auto">
          <nav className="px-5 py-4 flex flex-col" aria-label="Navigazione mobile">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label} className="border-b border-border/60">
                  <button
                    data-testid={`mobile-nav-${item.label.toLowerCase()}-trigger`}
                    className="w-full flex items-center justify-between py-4 text-base font-medium"
                    onClick={() => setExpanded(expanded === item.label ? null : item.label)}
                    aria-expanded={expanded === item.label}
                  >
                    {item.label}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded === item.label ? 'rotate-180' : ''}`} />
                  </button>
                  {expanded === item.label && (
                    <div className="pb-3 flex flex-col">
                      {item.children.map((c) => {
                        const Icon = ICON_MAP[c.href] || Sparkles;
                        return (
                          <LangLink
                            key={c.href}
                            to={c.href}
                            onClick={() => setOpen(false)}
                            className="py-2.5 pl-2 flex items-center gap-3 text-sm text-foreground/70 hover:text-primary transition-colors duration-150"
                          >
                            <span className="bg-brand-pale p-1.5 rounded-md">
                              <Icon className="h-3.5 w-3.5 text-primary" />
                            </span>
                            <span>
                              <span className="block font-medium">{c.label}</span>
                              {c.description && <span className="block mt-0.5 text-xs leading-relaxed text-foreground/50">{c.description}</span>}
                            </span>
                          </LangLink>
                        );
                      })}
                      {item.href === '/guide' && (
                        <LangLink
                          to="/guide"
                          onClick={() => setOpen(false)}
                          className="py-2.5 pl-2 flex items-center gap-3 text-sm font-semibold text-primary"
                        >
                          <ArrowUpRight className="h-3.5 w-3.5" />
                          {t('allGuides')}
                        </LangLink>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <LangLink
                  key={item.label}
                  to={item.href}
                  onClick={() => setOpen(false)}
                  className={`py-4 text-base font-medium border-b border-border/60 ${location.pathname === lp(item.href) ? 'text-primary' : ''}`}
                >
                  {item.label}
                </LangLink>
              )
            )}
            <LangLink
              to="/prenota"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-button"
              className="mt-5 mb-2 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3.5 text-sm font-semibold rounded-lg"
            >
              {t('ctaHeader')}
              <ArrowRight className="h-4 w-4" />
            </LangLink>
          </nav>
        </div>
      )}
    </header>
  );
};
